import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { ME_KEY, useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { DialogLayer } from '../ui/Dialog';
import { NumberInput } from '../ui/NumberInput';
import { formatDayRange } from './BusyList';

/**
 * „Blokady” of a catalog item (owner 2026-10-24 15:29: „możliwość blokowania sprzętu tak żeby był zarezerwowany i
 * wykrywany w kolizjach poza wyceną”). A block holds a quantity of the item over whole days, for a reason, outside any
 * event: the gear is out of the warehouse, events that add it get „Dodaj mimo to” and a shortage to rent. Shown in the
 * item's details and on „Dostępność”; not in the event calendar.
 */

export const BLOCKS_RESOURCE = 'availability.blocks';
const KEY = ['availability'] as const;

/** Fields a role may not see are absent (rule 7a: the reason and who made it). */
export type Block = { id: string; itemId: string; quantity: number; firstDay?: string; lastDay?: string; reason?: string; createdByName?: string; missing: number; version: number };
type Saved = { block: Block; missing: number };
type Draft = { quantity: string; firstDay: string; lastDay: string; reason: string };

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** What is wrong with the draft (shown before anything is sent), or null. */
function problemOf(d: Draft): string | null {
  if (!/^\d+$/.test(d.quantity.trim()) || Number(d.quantity) < 1) return 'Ilość musi być liczbą całkowitą większą od zera';
  if (!DAY_RE.test(d.firstDay) || !DAY_RE.test(d.lastDay)) return 'Podaj pierwszy i ostatni dzień';
  if (d.lastDay < d.firstDay) return 'Ostatni dzień nie może być przed pierwszym';
  if (d.reason.trim() === '') return 'Podaj powód blokady';
  return null;
}

/** The warning after a save: more was blocked than is free on those days (sure reservations or earlier blocks hold it). */
const missingText = (n: number) => `Brakuje ${n} szt. — w tych dniach nie ma tylu wolnych (zajmują je rezerwacje pewne albo wcześniejsze blokady).`;

function BlockForm({ title, submitLabel, initial, pending, error, onSubmit, onCancel }: {
  title: string;
  submitLabel: string;
  initial: Draft;
  pending: boolean;
  error: unknown;
  onSubmit: (d: Draft) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [problem, setProblem] = useState<string | null>(null);
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const apiError = error instanceof ApiError ? error : null;
  function submit(e: FormEvent) {
    e.preventDefault();
    const p = problemOf(draft);
    setProblem(p);
    if (!p) onSubmit({ ...draft, reason: draft.reason.trim() });
  }
  return (
    <form className="inline-form" aria-label={title} onSubmit={submit}>
      {problem && (
        <p className="alert" role="alert">
          {problem}
        </p>
      )}
      {!problem && apiError && apiError.status !== 409 && (
        <p className="alert" role="alert">
          {apiError.message}
          {fieldIssues(apiError).map((i) => (
            <span key={i.path + i.message}> {i.message}</span>
          ))}
        </p>
      )}
      <label className="field">
        <span>Ilość</span>
        <NumberInput kind="integer" value={draft.quantity} onValueChange={(quantity) => set({ quantity })} />
      </label>
      <div className="filters">
        <label className="field">
          <span>Pierwszy dzień</span>
          <input type="date" value={draft.firstDay} onChange={(e) => set({ firstDay: e.target.value })} />
        </label>
        <label className="field">
          <span>Ostatni dzień</span>
          <input type="date" value={draft.lastDay} onChange={(e) => set({ lastDay: e.target.value })} />
        </label>
      </div>
      <label className="field">
        <span>Powód</span>
        <input type="text" value={draft.reason} maxLength={200} placeholder="np. serwis, wypożyczone poza wyceną" onChange={(e) => set({ reason: e.target.value })} />
      </label>
      <div className="actions">
        <button className="btn primary" type="submit" disabled={pending}>
          {pending ? 'Zapisywanie…' : submitLabel}
        </button>
        <button className="btn ghost" type="button" onClick={onCancel}>
          Anuluj
        </button>
      </div>
    </form>
  );
}

export function BlocksSection({ itemId, itemName }: { itemId: string; itemName: string }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const visible = me !== undefined && me !== null && can(me, BLOCKS_RESOURCE, 'view');
  const list = useQuery({
    queryKey: [...KEY, 'blocks', itemId],
    enabled: visible,
    queryFn: () => api<{ blocks?: Block[] }>(`/api/availability/blocks?itemId=${encodeURIComponent(itemId)}`),
  });
  const [mode, setMode] = useState<{ kind: 'new' } | { kind: 'edit'; block: Block } | null>(null);
  const [warning, setWarning] = useState(0);
  // review 5: „Usunąć blokadę „<powód>”?” before it goes
  const [asking, setAsking] = useState<Block | null>(null);
  // the pieces a block holds change what events lack (packlists, quotes, availability): all of it is read again — but
  // not the signed-in account
  const refresh = () => queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== ME_KEY[0] });
  const save = useMutation({
    mutationFn: (d: Draft) => {
      const body = { quantity: Number(d.quantity), firstDay: d.firstDay, lastDay: d.lastDay, reason: d.reason };
      return mode?.kind === 'edit'
        ? api<Saved>(`/api/availability/blocks/${mode.block.id}`, { method: 'PATCH', body: { version: mode.block.version, ...body } })
        : api<Saved>('/api/availability/blocks', { method: 'POST', body: { itemId, ...body } });
    },
    onSuccess: (saved) => {
      setWarning(saved.missing);
      setMode(null);
    },
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (b: Block) => api(`/api/availability/blocks/${b.id}?version=${b.version}`, { method: 'DELETE' }),
    onSettled: refresh,
  });
  if (!me || !visible) return null;
  const blocks = list.data?.blocks ?? [];
  const conflict = [save.error, remove.error].some((e) => e instanceof ApiError && e.status === 409);
  const label = (b: Block) => b.reason || `${b.quantity} szt.`;
  const seesReason = fieldVisible(me, BLOCKS_RESOURCE, 'reason');
  return (
    <section aria-label="Blokady" className="dsec" data-feature-id="availability.blocks">
      <h3>Blokady</h3>
      {conflict && (
        <ConflictNotice
          onReload={() => {
            save.reset();
            remove.reset();
            void refresh();
          }}
        />
      )}
      {remove.error instanceof ApiError && remove.error.status !== 409 && (
        <p className="alert" role="alert">
          {remove.error.message}
        </p>
      )}
      {warning > 0 && (
        <p className="alert busy-long" role="alert">
          Zapisano blokadę. {missingText(warning)}
        </p>
      )}
      {blocks.length === 0 ? (
        <p className="muted">Brak blokad.</p>
      ) : (
        <ul className="list">
          {blocks.map((b) => (
            <li key={b.id} className="row">
              <div className="row-main">
                <strong>{b.quantity} szt.</strong> {formatDayRange(b.firstDay, b.lastDay)}
                {b.reason && ` · ${b.reason}`}
                {b.createdByName && <span className="muted"> · zablokował(a): {b.createdByName}</span>}
                {b.missing > 0 && (
                  <div>
                    <span className="busy-long">Brakuje {b.missing} szt.</span>
                  </div>
                )}
              </div>
              {can(me, BLOCKS_RESOURCE, 'edit') && seesReason && (
                <button
                  className="btn small"
                  type="button"
                  aria-label={`Zmień blokadę: ${label(b)}`}
                  onClick={() => {
                    save.reset();
                    setWarning(0);
                    setMode({ kind: 'edit', block: b });
                  }}
                >
                  Zmień
                </button>
              )}
              {can(me, BLOCKS_RESOURCE, 'delete') && (
                <button className="btn small ghost" type="button" aria-label={`Usuń blokadę: ${label(b)}`} disabled={remove.isPending} onClick={() => setAsking(b)}>
                  Usuń
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {asking && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="block-delete-title">
            <DialogLayer onClose={() => setAsking(null)} />
            <h2 id="block-delete-title">{`Usunąć blokadę „${label(asking)}”?`}</h2>
            <p>Zablokowane sztuki od razu wrócą do dostępnych w tych dniach.</p>
            <div className="actions">
              <button
                className="btn primary"
                type="button"
                autoFocus
                onClick={() => {
                  remove.mutate(asking);
                  setAsking(null);
                }}
              >
                Usuń blokadę
              </button>
              <button className="btn" type="button" onClick={() => setAsking(null)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {mode?.kind === 'edit' && (
        <BlockForm
          key={mode.block.id}
          title={`Zmień blokadę: ${itemName}`}
          submitLabel="Zapisz"
          initial={{ quantity: String(mode.block.quantity), firstDay: mode.block.firstDay ?? '', lastDay: mode.block.lastDay ?? '', reason: mode.block.reason ?? '' }}
          pending={save.isPending}
          error={save.error}
          onSubmit={(d) => save.mutate(d)}
          onCancel={() => setMode(null)}
        />
      )}
      {mode?.kind === 'new' && (
        <BlockForm
          title={`Zablokuj: ${itemName}`}
          submitLabel="Zablokuj"
          initial={{ quantity: '1', firstDay: '', lastDay: '', reason: '' }}
          pending={save.isPending}
          error={save.error}
          onSubmit={(d) => save.mutate(d)}
          onCancel={() => setMode(null)}
        />
      )}
      {mode === null && can(me, BLOCKS_RESOURCE, 'create') && (
        <div className="actions">
          <button
            className="btn small"
            type="button"
            onClick={() => {
              save.reset();
              setWarning(0);
              setMode({ kind: 'new' });
            }}
          >
            + Zablokuj
          </button>
        </div>
      )}
    </section>
  );
}
